import React, { useState, useEffect, useCallback } from 'react';
import { marketsApi } from '../../services/api';
import AdminPageHeader from '../../components/AdminPageHeader';
import './Markets.css';

const MARKET_TYPES = [
  { value: 'ar_sells_cc', label: 'AR Sells CC' },
  { value: 'ar_cc_separate', label: 'AR + CC Separate' },
  { value: 'cc_only', label: 'CC Only' },
  { value: 'ar_only', label: 'AR Only' },
];

const KEG_SETUPS = [
  { value: 'kegerator', label: 'Kegerator' },
  { value: 'individual', label: 'Individual Kegs' },
  { value: 'cans_only', label: 'Cans Only' },
  { value: 'krauts_only', label: 'Krauts Only' },
  { value: 'none', label: 'None' },
];

const DAYS = ['Saturday', 'Sunday', 'Tuesday', 'Daily'];

const EMPTY_FORM = {
  name: '', day_of_week: 'Saturday', type: 'ar_sells_cc',
  has_ar_van: false, keg_setup: 'none',
  has_mileage_reimbursement: false, is_active: true,
};

function typeLabel(type) {
  return MARKET_TYPES.find((t) => t.value === type)?.label || type;
}

function typePill(type) {
  const classes = {
    ar_sells_cc: 'pill-orange', ar_cc_separate: 'pill-blue',
    cc_only: 'pill-teal', ar_only: 'pill-green'
  };
  return <span className={`pill ${classes[type] || ''}`}>{typeLabel(type)}</span>;
}

export default function Markets() {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await marketsApi.getAll();
      setMarkets(data.markets || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openNew() { setForm(EMPTY_FORM); setEditing({}); }
  function openEdit(market) { setForm({ ...market }); setEditing(market); }
  function closeForm() { setEditing(null); setForm(EMPTY_FORM); }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing?.id) { await marketsApi.update(form); }
      else { await marketsApi.create(form); }
      await load();
      closeForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(market) {
    try {
      await marketsApi.toggleActive(market.id, !market.is_active);
      setMarkets((ms) => ms.map((m) => m.id === market.id ? { ...m, is_active: !m.is_active } : m));
    } catch (err) {
      setError(err.message);
    }
  }

  const filtered = markets.filter((m) => {
    if (filter === 'active') return m.is_active;
    if (filter === 'inactive') return !m.is_active;
    return true;
  });

  return (
    <div className="page">
      <AdminPageHeader
        title="Markets"
        subtitle={`${markets.filter((m) => m.is_active).length} active of ${markets.length} total`}
        action={<button className="btn-primary" onClick={openNew}>+ Add Market</button>}
      />
      <div className="page-body">
        {error && <div className="error-banner">{error} <button onClick={load}>Retry</button></div>}
        <div className="filter-bar">
          {['all', 'active', 'inactive'].map((f) => (
            <button key={f} className={`filter-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="loading-state">Loading markets...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            {markets.length === 0 ? 'No markets yet. Add your first market to get started.' : 'No markets match this filter.'}
          </div>
        ) : (
          <div className="markets-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Market</th><th>Day</th><th>Type</th>
                  <th>Keg Setup</th><th>AR Van</th><th>Mileage</th>
                  <th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.id} className={m.is_active ? '' : 'row-inactive'}>
                    <td className="td-name">{m.name}</td>
                    <td>{m.day_of_week}</td>
                    <td>{typePill(m.type)}</td>
                    <td>{KEG_SETUPS.find((k) => k.value === m.keg_setup)?.label || m.keg_setup}</td>
                    <td className="td-bool">{m.has_ar_van ? 'Y' : '-'}</td>
                    <td className="td-bool">{m.has_mileage_reimbursement ? 'Y' : '-'}</td>
                    <td>
                      <button className={`status-toggle ${m.is_active ? 'active' : 'inactive'}`} onClick={() => toggleActive(m)}>
                        {m.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td><button className="btn-edit" onClick={() => openEdit(m)}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing !== null && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing?.id ? 'Edit Market' : 'Add Market'}</h2>
              <button className="modal-close" onClick={closeForm}>x</button>
            </div>
            <form onSubmit={handleSave} className="modal-form">
              <div className="field">
                <label>Market Name *</label>
                <input name="name" value={form.name} onChange={handleChange} required placeholder="e.g. Monroe" autoFocus />
              </div>
              <div className="field-row">
                <div className="field">
                  <label>Day</label>
                  <select name="day_of_week" value={form.day_of_week} onChange={handleChange}>
                    {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Market Type</label>
                  <select name="type" value={form.type} onChange={handleChange}>
                    {MARKET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="field">
                <label>Keg Setup</label>
                <select name="keg_setup" value={form.keg_setup} onChange={handleChange}>
                  {KEG_SETUPS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </div>
              <div className="field-row checkboxes">
                <label className="checkbox-label">
                  <input type="checkbox" name="has_ar_van" checked={form.has_ar_van} onChange={handleChange} />
                  AR Van
                </label>
                <label className="checkbox-label">
                  <input type="checkbox" name="has_mileage_reimbursement" checked={form.has_mileage_reimbursement} onChange={handleChange} />
                  Mileage Reimbursement
                </label>
                <label className="checkbox-label">
                  <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} />
                  Active
                </label>
              </div>
              {error && <div className="form-error">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editing?.id ? 'Save Changes' : 'Add Market'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
